import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  VacationRequest,
  SimplifiedVacationRequest,
} from '../../../interfaces/holidays.interface';
import { QeqPipe } from 'src/app/shared/pipes/qeq-pipe/qeq-pippe.pipe';

interface DialogData {
  request: VacationRequest | SimplifiedVacationRequest;
}

@Component({
  selector: 'app-status-history-dialog',
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    QeqPipe,
  ],
  templateUrl: './status-history-dialog.component.html',
  styleUrls: ['./status-history-dialog.component.css', '../../../styles/layout.css'],
})
export class StatusHistoryDialogComponent {
  request: VacationRequest | SimplifiedVacationRequest;

  constructor(
    public dialogRef: MatDialogRef<StatusHistoryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {
    this.request = data.request;
  }

  close(): void {
    this.dialogRef.close();
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  // Type guard to check if request is SimplifiedVacationRequest
  isSimplifiedRequest(
    request: VacationRequest | SimplifiedVacationRequest
  ): request is SimplifiedVacationRequest {
    return 'start_date' in request && !('request_days' in request);
  }

  getRequestDaysDisplay(): string {
    if (this.isSimplifiedRequest(this.request)) {
      // SimplifiedVacationRequest format - dates already formatted as DD/MM/YYYY
      if (this.request.start_date === this.request.end_date) {
        return this.request.start_date;
      }
      return `${this.request.start_date} - ${this.request.end_date}`;
    } else {
      // VacationRequest format - has request_days array
      const requestDays = this.request.request_days;
      if (!requestDays || requestDays.length === 0) {
        return 'N/A';
      }

      const sortedDays = [...requestDays].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );

      const startDate = this.formatRequestDate(sortedDays[0].date);
      const endDate = this.formatRequestDate(
        sortedDays[sortedDays.length - 1].date
      );

      if (sortedDays.length === 1) {
        return `${startDate}`;
      }

      return `${startDate} - ${endDate}`;
    }
  }

  getDurationDays(): number {
    if (this.isSimplifiedRequest(this.request)) {
      return this.request.duration_days;
    } else {
      return this.request.request_days?.length || 0;
    }
  }

  private formatRequestDate(dateStr: string): string {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  }

  getRequestTypeDisplay(): string {
    return this.request.request_type === 'holidays'
      ? 'Vacaciones'
      : 'Día Personal';
  }

  getStatusDisplay(status: string): string {
    const statusMap: { [key: string]: string } = {
      pending: 'Pendiente',
      approved: 'Aprobado',
      rejected: 'Rechazado',
      cancelled: 'Cancelado',
    };
    return statusMap[status] || status;
  }
}
